import React, {useEffect, useMemo, useState} from 'react';
import {Dropdown as WCDropdown} from '@/web-components';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {Role} from '../../modals/time-registration/time-registration-modal/types/TimeRegistrationRole';
import {createFragmentContainer, graphql} from 'react-relay';
import {
	getCompanyRoles,
	isRoleDisabled,
} from '../../modals/time-registration/time-registration-modal/util/TimeRegistrationModalRoleUtil';
import {getDefaultRole} from '../../../util/time-registration/time-role/TimeRegistrationRoleUtil';
import {RoleDropdown_viewer} from './__generated__/RoleDropdown_viewer.graphql';

import {LabelAndWarning} from './LabelAndWarning';
import {FormattedHTMLMessage, FormattedMessage} from 'react-intl';
import {ComboBox, Text} from '@forecast-it/design-system';
import {DesignSystemComboBoxOldLookAdapter} from '../DesignSystemComboBoxOldLookAdapter';

function getWarningText(showDisabledRoleWarning: boolean, showMovedToProjectRoleWarning: boolean) {
	if (showMovedToProjectRoleWarning) {
		return <FormattedMessage id="time_reg.changed_project_role_changed" />;
	}

	if (showDisabledRoleWarning) {
		return <FormattedHTMLMessage id="project_person.deactivated_role" />;
	}

	return undefined;
}
type RoleDropdownRelayProps = {
	viewer: RoleDropdown_viewer;
};

export enum RoleDropdownLabelFormat {
	NO_MARGIN = 'narrow',
	DEFAULT = 'default',
}

export type RoleDropdownComponentProps = {
	role?: Role;
	showMovedToProjectRoleWarning?: boolean;
	onSelect: (role?: Role) => void;
	isDisabled?: boolean;
	projectId?: string;
	showLabel?: boolean;
	labelFormat?: RoleDropdownLabelFormat;
};

type RoleDropdownProps = RoleDropdownRelayProps & RoleDropdownComponentProps;

type RoleElement = {
	value: string;
	label: string | null;
	disabled?: boolean;
	cy: string;
};
export function onRoleOrRolesChanged(
	roles: Role[],
	selectedRole: Role | undefined
): {roleOptions: RoleElement[]; isRoleDisabled: boolean} {
	const roleOptions = roles.map(role => ({
		value: role.id,
		label: role.name,
		disabled: role.disabled,
		cy: 'role-dropdown-item-' + role.name,
	}));

	return {roleOptions, isRoleDisabled: isRoleDisabled(selectedRole?.id, roles)};
}

function RoleDropdown({
	viewer,
	role,
	showMovedToProjectRoleWarning,
	onSelect,
	isDisabled,
	projectId,
	showLabel,
	labelFormat,
}: RoleDropdownProps): JSX.Element {
	const [selectedRole, setSelectedRole] = useState<Role | undefined>(role);
	const [showDisabledRoleWarning, setShowDisabledRoleWarning] = useState<boolean>(false);

	const roles = useMemo(
		() => getCompanyRoles(viewer.company?.roles, projectId, selectedRole),
		[viewer.company?.roles, projectId, selectedRole]
	);

	useEffect(() => {
		setSelectedRole(
			getDefaultRole(role, viewer?.project?.projectPerson?.role, viewer?.task?.role, viewer.company?.person?.role) ||
				undefined
		);
	}, [role, projectId, viewer?.task, viewer?.project]);

	const roleOptions = useMemo(() => {
		const {roleOptions, isRoleDisabled} = onRoleOrRolesChanged(roles, selectedRole);
		setShowDisabledRoleWarning(isRoleDisabled);
		return roleOptions;
	}, [role, roles]);

	const handleTimeRegRoleChange = (id: string) => {
		const newRole = roles.find(role => role.id === id);
		onSelect(newRole);
		setSelectedRole(newRole);
		trackEvent('Time Registration', 'Role Changed', {
			timeRegistrationRoleName: newRole?.name,
		});
	};

	return (
		<LabelAndWarning
			showLabel={showLabel}
			labelFormat={labelFormat}
			warningText={getWarningText(showDisabledRoleWarning, showMovedToProjectRoleWarning || false)}
		>
			<DesignSystemComboBoxOldLookAdapter>
				<ComboBox
					data-cy={'role-dropdown'}
					defaultItems={roleOptions}
					value={selectedRole?.id}
					placeholder={'Select a role'}
					onChange={handleTimeRegRoleChange}
					width={ComboBox.WIDTH.FLEX}
					size={'small'}
					menuTrigger="focus"
					disabled={isDisabled}
					customClassName={'role-drop-down'}
				>
					{option => (
						<ComboBox.Item key={option.value} textValue={option.label || ''}>
							<Text data-cy={option.cy} noWrap={true} size={'2'}>
								{option.label}
							</Text>{' '}
						</ComboBox.Item>
					)}
				</ComboBox>
			</DesignSystemComboBoxOldLookAdapter>
		</LabelAndWarning>
	);
}

export type RoleDropdownPlaceholderProps = {
	showLabel?: boolean;
	labelFormat?: RoleDropdownLabelFormat;
};
function PlaceholderDropdown({showLabel, labelFormat}: RoleDropdownPlaceholderProps): JSX.Element {
	return (
		<LabelAndWarning showLabel={showLabel} labelFormat={labelFormat}>
			<WCDropdown name={''} inputClassName={'role-drop-down'} />
		</LabelAndWarning>
	);
}

const RoleDropdownQuery = graphql`
	query RoleDropdown_Query($projectId: ID, $taskId: ID, $personId: ID) {
		viewer {
			component(name: "role_dropdown")
			actualPersonId
			id
			...RoleDropdown_viewer @arguments(projectId: $projectId, taskId: $taskId, personId: $personId)
		}
	}
`;

export {RoleDropdownQuery, PlaceholderDropdown};

export default createFragmentContainer(RoleDropdown, {
	viewer: graphql`
		fragment RoleDropdown_viewer on Viewer
		@argumentDefinitions(projectId: {type: "ID"}, taskId: {type: "ID"}, personId: {type: "ID"}) {
			company {
				roles(includeDisabled: false, projectId: $projectId) {
					edges {
						node {
							id
							name
						}
					}
				}
				person(id: $personId) {
					id
					role {
						id
						name
					}
				}
			}
			project(internalId: $projectId) {
				projectPerson(personId: $personId) {
					role {
						id
						name
					}
				}
			}
			task(id: $taskId) {
				role {
					id
					name
				}
			}
		}
	`,
});
